import { useAuth } from 'react-oidc-context'
import { useEffect, type ReactNode } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'

interface ProtectedRouteProps {
  children: ReactNode
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const auth = useAuth()
  const navigate = useNavigate()
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname

  // After OIDC callback completes and auth is ready, redirect away from the
  // callback route to the dashboard. This runs once auth has fully settled.
  useEffect(() => {
    if (auth.isAuthenticated && !auth.isLoading && currentPath.includes('/auth/callback')) {
      // Extract slug from path: /t/{slug}/auth/callback
      const match = currentPath.match(/^\/t\/([^/]+)\/auth\/callback/)
      if (match && match[1]) {
        navigate({ to: '/t/$slug/risks', params: { slug: match[1] as string }, replace: true })
      }
    }
  }, [auth.isAuthenticated, auth.isLoading, currentPath, navigate])

  if (auth.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="mt-4 text-sm text-muted-foreground">Authenticating...</p>
        </div>
      </div>
    )
  }

  if (auth.error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="space-y-4 text-center">
          <h2 className="text-lg font-semibold text-destructive">Authentication Error</h2>
          <p className="text-sm text-muted-foreground">{auth.error.message}</p>
          <button
            onClick={() => auth.signinRedirect()}
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:opacity-90"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (!auth.isAuthenticated) {
    auth.signinRedirect()
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="mt-4 text-sm text-muted-foreground">Redirecting to login...</p>
        </div>
      </div>
    )
  }

  // Ensure we have a user object with an access token
  if (!auth.user?.access_token) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="mt-4 text-sm text-muted-foreground">Loading session...</p>
        </div>
      </div>
    )
  }

  // Check if the token has expired. If so, trigger silent renew or redirect.
  // This prevents firing API calls with stale tokens from sessionStorage.
  if (auth.user.expired) {
    // signinSilent will attempt to refresh; if it fails, the error handler above catches it
    auth.signinSilent().catch(() => {
      auth.signinRedirect()
    })
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="mt-4 text-sm text-muted-foreground">Refreshing session...</p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
